import { Component, computed, inject, input, OnInit, signal } from '@angular/core';

import { CommonModule } from '@angular/common';
import { take } from 'rxjs';
import {
  ISelfPayRateItem,
  SelfPayRateType,
  SelfPayRatesHttpService,
  SelfPayVisitType
} from '@sondermind/data-access/client-insurance';
import { IconColors, IconSizes, IrisIconComponent } from '@sondermind/iris/icon';
import { IrisSpinnerComponent } from '@sondermind/iris/spinner';

import { ISelfPayEstimateCategory } from '../../models/cost-estimation.model';

interface IDetailedEstimateLabelCategory {
  categoryLabel: string;
  itemLabels: string[];
}
const DETAILED_ESTIMATE_LABELS: IDetailedEstimateLabelCategory[] = [
  { categoryLabel: 'Talk therapy estimates', itemLabels: ['Intake session', '60 minute follow-up'] },
  { categoryLabel: 'Psychiatry estimates', itemLabels: ['Initial evaluation', '30 minute follow-up'] }
];

/**
 * Rate type(s) for each item in DETAILED_ESTIMATE_LABELS (category order, then item order).
 * A single type uses that rate's amount; an array means sum the amounts for "30 minute follow-up".
 */
const RATE_TYPE_BY_CATEGORY_ITEM: Array<SelfPayRateType | SelfPayRateType[]> = [
  SelfPayRateType.TALK_THERAPY_INTAKE,
  SelfPayRateType.TALK_THERAPY_FOLLOW_UP,
  SelfPayRateType.PSYCHIATRY_INTAKE,
  [SelfPayRateType.PSYCHIATRY_FOLLOW_UP, SelfPayRateType.PSYCHIATRY_FOLLOW_UP_ADD_ON]
];

@Component({
  selector: 'client-self-pay-cost-estimate-detailed',
  templateUrl: './self-pay-cost-estimate-detailed.component.html',
  styleUrls: ['./self-pay-cost-estimate-detailed.component.scss'],
  imports: [CommonModule, IrisIconComponent, IrisSpinnerComponent]
})
export class SelfPayCostEstimateDetailedComponent implements OnInit {
  readonly rates = input<ISelfPayRateItem[]>([]);
  readonly standAlone = input<boolean>(false);

  private readonly selfPayRatesHttp = inject(SelfPayRatesHttpService);

  private readonly standaloneRates = signal<ISelfPayRateItem[]>([]);
  readonly ratesLoading = signal<boolean>(false);

  readonly detailedEstimates = computed<ISelfPayEstimateCategory[]>(() => {
    const ratesToUse = this.standAlone() ? this.standaloneRates() : this.rates();
    return this.buildDetailedEstimatesFromRates(ratesToUse);
  });

  readonly expanded = signal<boolean>(false);

  readonly iconSizes = IconSizes;
  readonly iconColors = IconColors;

  ngOnInit(): void {
    if (this.standAlone()) {
      this.loadRates();
    }
  }

  /**
   * Fetches self-pay rates when the component is used standalone (no rates passed from parent).
   */
  private loadRates(): void {
    this.ratesLoading.set(true);
    this.selfPayRatesHttp
      .getSelfPayRates$(SelfPayVisitType.TELEHEALTH)
      .pipe(take(1))
      .subscribe({
        next: (fetchedRates) => {
          this.ratesLoading.set(false);
          this.standaloneRates.set(fetchedRates);
        },
        error: () => {
          this.ratesLoading.set(false);
        }
      });
  }

  toggle(): void {
    this.expanded.update((v) => !v);
  }

  /**
   * Build detailed estimate categories from API rates. Labels are static; only amounts come from rates.
   * "30 minute follow-up" under Psychiatry is the sum of PSYCHIATRY_FOLLOW_UP and PSYCHIATRY_FOLLOW_UP_ADD_ON.
   */
  private buildDetailedEstimatesFromRates(rates: ISelfPayRateItem[]): ISelfPayEstimateCategory[] {
    let itemIndex = 0;
    return DETAILED_ESTIMATE_LABELS.map((category) => ({
      categoryLabel: category.categoryLabel,
      items: category.itemLabels.map((label) => {
        const rateTypeOrTypes = RATE_TYPE_BY_CATEGORY_ITEM[itemIndex];
        itemIndex += 1;
        const amount = this.getAmountForRateTypes(rates, rateTypeOrTypes);
        return { label, amount };
      })
    }));
  }

  private getAmountForRateTypes(
    rates: ISelfPayRateItem[],
    typeOrTypes: SelfPayRateType | SelfPayRateType[]
  ): number | null {
    const types = Array.isArray(typeOrTypes) ? typeOrTypes : [typeOrTypes];
    const values = types.map((type) => {
      const rate = rates.find((r) => r.type === type);
      return rate?.amount?.amount;
    });
    if (values.some((v) => v == null)) {
      return null;
    }
    return (values as number[]).reduce((sum, v) => sum + v, 0);
  }
}
